/**
 * 资产素材库管理器
 * 负责管理资产素材库的业务操作
 * @author Joey
 */

import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { useToast } from "@/hooks/use-toast";
import { UserMaterialFolderData, userMaterialListFolders, UserMaterialListFoldersData } from "@/services/user-material-list-folders";
import { userMaterialRenameFolder } from "@/services/user-material-rename-folder";
import { userMaterialFolderCreate } from "@/services/user-material-create-folder";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatch, useParams } from "react-router-dom";
import { userMaterialDeleteFolder } from "@/services/user-material-delete-folder";
import { jsonStore } from "@/manager/cache/localdb";
import { assetsKnowledgeManager } from "./assets-knowledge-manager";
import { userMaterialGetFolderItems } from "@/services/user-material-get-folder-items";
import { FileItem, useDownload } from "@/hooks/use-download";
import { agentsHooks } from "..";
import { AllFolderItemData, userMaterialGetAllFolderItems } from "@/services/user-material-get-all-folder-items";
import { agentManager } from "./agent-manager";

// 缓存数据结构
interface CacheData {
  materialFolders: UserMaterialFolderData[];
  page?: number;
  pageSize?: number;
  total?: number;
}

interface MaterialFolderSearchForm {
  page: number;
  pageSize: number;
  total: number;
}

interface AssetsMaterialData {
  id: string;
  name: string;
  time: string;
}

type AssetsMaterialsChangeEventDetail = {
  assetsMaterials: AssetsMaterialData[];
};
type AssetsMaterialsChangeEvent = CustomEvent<AssetsMaterialsChangeEventDetail>;

type MaterialFoldersChangeEventDetail = {
  materialFolders: UserMaterialFolderData[];
}
type MaterialFoldersChangeEvent = CustomEvent<MaterialFoldersChangeEventDetail>;

type MaterialFolderSearchFormChangeEventDetail = {
  materialFolderSearchForm: MaterialFolderSearchForm;
}
type MaterialFolderSearchFormChangeEvent = CustomEvent<MaterialFolderSearchFormChangeEventDetail>;

class AssetsMaterialLocal {
  constructor(private storageKey: string) { }
  async get(): Promise<CacheData | null> {
    return await jsonStore.get<CacheData>(this.storageKey);
  }
  async set(data: CacheData): Promise<void> {
    await jsonStore.set(this.storageKey, data);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AssetsMaterialRemote {
  constructor(private cache: AssetsMaterialLocal) { }
  async getCached() {
    return await this.cache.get();
  }
  async fetchMaterialFolders(page: number, pageSize: number, userId?: string) {
    const data = await userMaterialListFolders({ page, pageSize, userId });
    await this.cache.set({ materialFolders: data.list, page: data.page, pageSize: data.size, total: data.total });
    return { list: data.list, page: data.page, pageSize: data.size, total: data.total };
  }
  // async fetchFolders(page?: number, pageSize?: number, userId?: string) {
  //   const data = await userMaterialGetAllFolderItems({});
  //   await this.cache.set({ materialFolders: data.folderList });
  //   return { list: data.folderList };
  // }
  async fetchMaterialItems(folderId: number) {
    const data = await userMaterialGetFolderItems(folderId);
    return data.items || [];
  }
  async createFolder(folderName: string) {
    return userMaterialFolderCreate({ parentFolderId: 0, folderName });
  }
  async renameFolder(folderId: number, folderName: string) {
    return userMaterialRenameFolder({ folderId, folderName });
  }
  async deleteFolder(ids: number[]) {
    return userMaterialDeleteFolder(ids);
  }
}

export class AssetsMaterialManager {
  private static instance: AssetsMaterialManager;
  private readonly STORAGE_KEY = "MIUTA_ASSETS_MATERIAL";
  private activeStorageKey = "";
  private materialFolders: UserMaterialFolderData[] = [];
  private materialFolderSearchForm: MaterialFolderSearchForm = {
    page: 1,
    pageSize: 100,
    total: 0,
  }
  private cache: AssetsMaterialLocal;
  private api: AssetsMaterialRemote;

  private constructor() {
    this.cache = new AssetsMaterialLocal(this.getStorageKey());
    this.api = new AssetsMaterialRemote(this.cache);
    this.activeStorageKey = this.getStorageKey();
  }

  public static getInstance(): AssetsMaterialManager {
    if (!AssetsMaterialManager.instance) {
      AssetsMaterialManager.instance = new AssetsMaterialManager();
    }
    return AssetsMaterialManager.instance;
  }

  private getStorageKey() {
    const agentId = agentManager.getAgentId() || "";
    return `${this.STORAGE_KEY}:${agentId}`;
  }

  private getMaterialFoldersChangeEventName() {
    return `${this.STORAGE_KEY}:material-folders-change`;
  }

  private getMaterialFormChangeEventName() {
    return `${this.STORAGE_KEY}:material-form-change`;
  }

  private ensureStorageScope() {
    const nextStorageKey = this.getStorageKey();
    if (nextStorageKey === this.activeStorageKey) {
      return;
    }
    this.activeStorageKey = nextStorageKey;
    this.cache = new AssetsMaterialLocal(nextStorageKey);
    this.api = new AssetsMaterialRemote(this.cache);
  }

  // 初始化
  public async init() {
    this.ensureStorageScope();
    try {
      const cached = await this.api.getCached();
      if (cached && cached.materialFolders && cached.materialFolders.length) {
        this.setMaterialFolders(cached.materialFolders);
        this.setMaterialFolderSearchForm({
          page: cached.page || this.materialFolderSearchForm.page,
          pageSize: cached.pageSize || this.materialFolderSearchForm.pageSize,
          total: cached.total || 0,
        });
        this.fetchMaterialFolders();
        return;
      }
    } catch (error) {
      console.error("get cache data failed", error);
      this.setMaterialFolders([]);
    }
    await this.fetchMaterialFolders();
  }

  // ==================== 素材库操作 ====================
  // 请求素材库目录数据
  public async fetchMaterialFolders() {
    this.ensureStorageScope();
    try {
      const data = await this.api.fetchMaterialFolders(1, 1000);
      this.setMaterialFolders(data.list);
    } catch (error) {
      console.error("Failed to fetch assets material list:", error);
      this.dispatchMaterialFoldersChange();
      throw error;
    }
  }

  // 请求素材库目录内文件数据
  public async fetchMaterialItem(folderId: number) {
    this.ensureStorageScope();
    try {
      const items = await this.api.fetchMaterialItems(folderId);
      return items;
    } catch (error) {
      console.error("Failed to fetch assets material item list:", error);
      throw error;
    }
  }

  // 创建素材库目录
  public async materialCreate(folderName: string) {
    this.ensureStorageScope();
    try {
      const res = await this.api.createFolder(folderName);
      const materials = [res.folder, ...this.materialFolders];
      this.setMaterialFolders(materials);
      return res;
    } catch (error) {
      console.error("Failed to create assets material folder:", error);
      this.dispatchMaterialFoldersChange();
      throw error;
    }
  }

  // 重命名素材目录
  public async materialRename({
    folderId,
    folderName
  }: {
    folderId: number,
    folderName: string
  }) {
    this.ensureStorageScope();
    const originMaterials = [...this.materialFolders];
    try {
      const materials = this.materialFolders.map((item) => {
        if (item.id === folderId) {
          return { ...item, folderName };
        }
        return item;
      });
      this.setMaterialFolders(materials);
      const res = await this.api.renameFolder(folderId, folderName);
      return res;
    } catch (error) {
      this.setMaterialFolders(originMaterials);
      throw error;
    }
  }

  // 删除素材目录
  public async materialDelete(ids: number[]) {
    this.ensureStorageScope();
    const originMaterials = [...this.materialFolders];
    try {
      await this.api.deleteFolder(ids);
    } catch (error) {
      this.setMaterialFolders(originMaterials);
      throw error;
    }
  }

  public clear() {
    this.ensureStorageScope();
    this.setMaterialFolders([]);
  }

  // ==================== 读取写入操作 ====================
  public getMaterialFolders() {
    return this.materialFolders;
  }
  public setMaterialFolders(list: UserMaterialFolderData[]) {
    this.ensureStorageScope();
    this.materialFolders = list;
    this.dispatchMaterialFoldersChange();
    this.cache.set({
      materialFolders: this.materialFolders,
      page: this.materialFolderSearchForm.page,
      pageSize: this.materialFolderSearchForm.pageSize,
      total: this.materialFolderSearchForm.total,
    });
  }

  public setMaterialFolderSearchForm(form: MaterialFolderSearchForm) {
    this.ensureStorageScope();
    this.materialFolderSearchForm = form;
    this.dispatchMaterialFolderSearchFormChange();
  }
  public getMaterialFolderSearchForm() {
    return this.materialFolderSearchForm;
  }


  // ==================== 订阅通知操作 ====================
  // 监听目录列表变化事件
  private dispatchMaterialFoldersChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<MaterialFoldersChangeEventDetail>(this.getMaterialFoldersChangeEventName(), {
      detail: { materialFolders: this.materialFolders }
    });
    window.dispatchEvent(event);
  }
  public onMaterialFoldersChange(listener: (e: MaterialFoldersChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = this.getMaterialFoldersChangeEventName();
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听请求表单变化事件
  private dispatchMaterialFolderSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<MaterialFolderSearchFormChangeEventDetail>(this.getMaterialFormChangeEventName(), {
      detail: { materialFolderSearchForm: this.materialFolderSearchForm }
    });
    window.dispatchEvent(event);
  }
  public onMaterialFolderSearchFormChange(listener: (e: MaterialFolderSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = this.getMaterialFormChangeEventName();
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// =============================== Hooks ===============================
// ================= 素材库 =================
// 资产素材库目录列表数据
export const useMaterialFoldersData = () => {
  const toast = useToast();

  const [materialFolders, setMaterialFolders] = useState(assetsMaterialManager.getMaterialFolders());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    try {
      setLoading(true);
      await assetsMaterialManager.init();
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = assetsMaterialManager.onMaterialFoldersChange((e) => {
      setMaterialFolders(e.detail.materialFolders)
    })
    return unsubscribe
  }, [])

  return {
    materialFolders,
    loading,
  }
}

// 资产目录列表侧边栏数据
export const useAssetsSidebarData = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useI18nNavigate();
  const location = useLocation();

  const { id } = useParams();

  const isMaterial = useMatch('/assets/material/');
  const isMaterialItem = useMatch('/assets/material/:id');
  const isKnowledge = useMatch('/assets/knowledge/');
  const isKnowledgeItem = useMatch('/assets/knowledge/:id');
  const isActor = useMatch('/assets/actor/');
  const isVoice = useMatch('/assets/voice/');

  const [sidebarCollapsed, setSidebarCollapsed] = useState<Record<string, boolean>>({});
  const [active, setActive] = useState('');

  const [materialFolders, setMaterialFolders] = useState(assetsMaterialManager.getMaterialFolders());
  const [knowledgeFolders, setKnowledgeFolders] = useState(assetsKnowledgeManager.getKnowledgeFolders());

  // const isAgentData = false
  const { isAgentData } = agentsHooks.useGetAgentList();

  // 辅助函数：处理子菜单截断逻辑
  const formatChildren = (folders: AllFolderItemData[], parentId: string) => {
    const list = folders.map(folder => ({
      id: folder.id.toString(),
      name: folder.folderName,
    }));
    if (list.length > 5) {
      return [
        ...list.slice(0, 5),
        // 增加 more 项，用于触发展开更多子项
        {
          id: 'more',
          name: t('assets.material.view_more'),
          parentId,  // 增加 parentId 方便定位
        }
      ];
    }
    return list;
  };

  // 组装侧边栏数据
  const sidebar = useMemo(() => {
    return [
      {
        id: 'material',
        name: t('assets.material_title'),
        path: '/assets/material/',
        children: formatChildren(materialFolders, 'material'),
      },
      {
        id: 'knowledge',
        name: t('assets.knowledge_title'),
        path: '/assets/knowledge/',
        children: formatChildren(knowledgeFolders, 'knowledge'),
      },
      {
        id: 'actor',
        name: t('assets.actor'),
        path: '/assets/actor/',
      },
      {
        id: 'voice',
        name: t('assets.voice'),
        path: '/assets/voice/',
      }
    ]
  }, [materialFolders, knowledgeFolders, t]);

  // 格式化sidebar数据，将children.id转换为数组
  const sidebarMap = useMemo(() => {
    return {
      material: sidebar.find(item => item.id === 'material')?.children.map(child => child.id),
      knowledge: sidebar.find(item => item.id === 'knowledge')?.children.map(child => child.id),
      actor: [],
      voice: [],
    }
  }, [sidebar])

  // 将sidebar数组转换为以id为key的对象格式
  const sidebarKeyMap = useMemo(() => {
    return sidebar.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [sidebar])

  // 将sidebar中所有children项进行id映射
  const childrenKeyMap = useMemo(() => {
    return sidebar.reduce((acc, item) => {
      if (item.children) {
        item.children.forEach(child => {
          acc[child.id] = child;
        });
      }
      return acc;
    }, {});
  }, [sidebar]);

  // 自动展开逻辑：仅当 active 变化时，确保对应的父级是展开的，但不关闭其他已展开的
  useLayoutEffect(() => {
    if (!isAgentData) return;

    const parentId = Object.keys(sidebarMap).find(key =>
      active === key || sidebarMap[key]?.includes(active)
    );

    if (parentId) {
      setSidebarCollapsed(prev => ({ ...prev, [parentId]: true }));
    }
  }, [active, sidebarMap]);

  const checkAgentData = () => {
    if (!isAgentData) {
      // navigate('/assets/');
      return;
    }
  }

  // 监听路由变化，更新active状态
  useLayoutEffect(() => {
    if (location.pathname === '/') {
      return;
    }
    // 初始化素材目录
    if (!assetsMaterialManager.getMaterialFolders().length) {
      assetsMaterialManager.init();
    }
    // 初始化知识库目录
    if (!assetsKnowledgeManager.getKnowledgeFolders().length) {
      assetsKnowledgeManager.init();
    }

    if (isMaterial) {
      checkAgentData();
      setActive('material');
      return
    }
    if (isKnowledge) {
      checkAgentData();
      setActive('knowledge');
      return;
    }
    if (isActor) {
      checkAgentData();
      setActive('actor');
      return;
    }
    if (isVoice) {
      checkAgentData();
      setActive('voice');
      return;
    }
    if (isMaterialItem && id) {
      checkAgentData();
      setActive(id);
      return;
    }
    if (isKnowledgeItem && id) {
      checkAgentData();
      setActive(id);
      return;
    }
    // 其他情况，清除active
    setActive('')
  }, [location.pathname]);

  // 处理侧边栏折叠状态（后续可考虑缓存状态）
  const handleCollapseChange = (id: string) => {
    setSidebarCollapsed(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // 处理一级选项点击事件
  const handleParentClick = (id: string) => {
    if (!isAgentData) {
      navigate('/unsubscribe/');
      return;
    };
    navigate(sidebarKeyMap[id].path);
  };

  // 处理二级选项点击事件
  const handleChildrenClick = (id: string, pid: string) => {
    if (!isAgentData) {
      navigate('/unsubscribe/');
      return;
    }
    // 如果是 'more' 节点，跳转到父级页面
    if (id === 'more') {
      handleParentClick(pid);
      return;
    }
    if (!sidebarKeyMap[pid]) return;
    navigate(`/assets/${sidebarKeyMap[pid].id}/${id}/`);
  }

  useEffect(() => {
    const unsubscribe = assetsMaterialManager.onMaterialFoldersChange((e) => {
      setMaterialFolders(e.detail.materialFolders)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = assetsKnowledgeManager.onKnowledgeFoldersChange((e) => {
      setKnowledgeFolders(e.detail.knowledgeFolders)
    })
    return unsubscribe
  }, [])

  return {
    isAgentData,
    sidebar,
    sidebarMap,
    sidebarKeyMap,
    childrenKeyMap,
    active,
    sidebarCollapsed,
    materialFolders,
    knowledgeFolders,

    setActive,
    setSidebarCollapsed,
    handleParentClick,
    handleChildrenClick,
    handleCollapseChange,
  }
}

// 创建素材目录
export const useMaterialFolderCreate = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const createMaterial = async ({ name }: { name: string }) => {
    try {
      setLoading(true);
      const res = await assetsMaterialManager.materialCreate(name);
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    createMaterial,
    loading,
  }
}

// 重命名素材目录
export const useMaterialFolderRename = () => {
  const { showToastByCode } = useToast();
  const params = useParams();

  const renameMaterial = async ({ id, name }: { id?: number, name: string }) => {
    try {
      const res = await assetsMaterialManager.materialRename({
        folderId: id || Number(params.id),
        folderName: name
      });
      // // 重名检查
      // if (res.isDuplicate) {
      //   error({ description: 'Material name already exists' });
      // }
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    }
  };

  return {
    renameMaterial,
  };
};

// 删除素材目录
export const useMaterialFolderDelete = () => {
  const { t } = useTranslation();
  const { toast, showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const deleteMaterial = async (id: number | number[]) => {
    try {
      setLoading(true);
      id = Array.isArray(id) ? id : [id];
      await assetsMaterialManager.materialDelete(id);
      // 刷新素材目录列表
      await assetsMaterialManager.fetchMaterialFolders();
    } catch (error) {
      const errorMsg = error.message;
      const match = errorMsg.match(/ID\s+(\d+)\s+or/);
      if (match) {
        const usedFolderId = Number(match[1]);
        const usedFolder = assetsMaterialManager.getMaterialFolders().find(f => f.id === usedFolderId);
        if (usedFolder) {
          toast.default({
            description: t('assets.material.delete_used_folder', { folderName: usedFolder.folderName }),
          });
        }
      } else {
        showToastByCode(error);
      }
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteMaterial,
    loading,
  };
};

// 下载素材库目录文件
export const useDownloadMaterialFolder = () => {
  const { showToastByCode } = useToast();
  const { downloadFileToZip } = useDownload();
  const [preparing, setPreparing] = useState(false);

  const downloadMaterialFolder = async (ids: number[]) => {
    try {
      const folderMap: Record<string, FileItem[]> = {};
      setPreparing(true);

      // 获取所有目录信息
      const folders = assetsMaterialManager.getMaterialFolders();

      for (const id of ids) {
        const folder = folders.find(f => f.id === id);
        if (!folder) continue;

        // 拉取该目录下的文件
        const items = await assetsMaterialManager.fetchMaterialItem(id);

        const files: FileItem[] = items.map(item => ({
          url: item.itemUrl,
          filename: item.itemUrl.split('/').pop() || 'file',
        }));

        if (files.length > 0) {
          folderMap[folder.folderName] = files;
        }
      }
      setPreparing(false);

      await downloadFileToZip(folderMap, 'materials.zip');
    } catch (error) {
      showToastByCode(error);
      throw error;
    }
  };

  return {
    downloadMaterialFolder,
    preparing,
  };
};

export const assetsMaterialHooks = {
  useAssetsSidebarData,
  // 素材库
  useMaterialFoldersData,
  useMaterialFolderCreate,
  useMaterialFolderRename,
  useMaterialFolderDelete,
  useDownloadMaterialFolder,
}

// 导出单例实例
export const assetsMaterialManager = AssetsMaterialManager.getInstance();
